import { FormLabel, TextInput, Blockquote, InlineLoading, Checkbox } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import ExcelUploader from "components/Shared/ExcelUploader/ExcelUploader"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import useCommon from "hooks/useCommon"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Select, { SingleValue } from "react-select"
import {
  useGetBaseSchemasQuery,
  useSaveXlsFormMutation,
  useUploadXlsFormFileMutation,
} from "services/household/xlsFormSchemas"
import { useGetAllCategoriesQuery } from "services/household/categories"
import { useGetSubCategoriesQuery } from "services/household/subCategories"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { XlsFormResult } from "interfaces/xlsForm"
import ValidationFormSchema from "pages/XlsForm/ValidationFormSchema"
import { XlsFormStatus } from "constants/constants"
import XlsFormFieldMapping from "components/XlsForm/XlsFormFieldMapping"
import { FieldsData } from "interfaces/mappingInterfaces"
import { CommonGetOptions } from "interfaces/dataInterfaces"

export default function AddXlsFormSchema() {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()
  const validationRef = useRef<HTMLDivElement>(null)

  const [saveXls, { data: fileHeaders, reset }] = useUploadXlsFormFileMutation()
  const [saveXlsForm, { isLoading }] = useSaveXlsFormMutation()
  const { data: baseSchemas, isFetching: loadingSchemas } = useGetBaseSchemasQuery()

  const [name, setName] = useState("")
  const [xlsFormsIsArchived, setxlsFormsIsArchived] = useState<boolean>(false)
  const [file, setFile] = useState<File>()
  const [currentCategory, setCurrentCategory] = useState<CommonGetOptions | null>(null)
  const [currentSubCategory, setCurrentSubCategory] = useState<CommonGetOptions | null>(null)
  const [baseSchemaId, setBaseSchemaId] = useState<number | undefined>(undefined)
  const [adm0code, setAdm0code] = useState<number | undefined>(undefined)
  const [errorSaveXls, setErrorSaveXls] = useState<XlsFormResult | null>(null)
  const [options, setOptions] = useState<CommonGetOptions[]>([])
  const [fieldsData, setFieldsData] = useState<FieldsData>({
    questionLabelPosition: 0,
    choiceLabelPosition: 0,
  })

  const { data: categories, isFetching: loadingCategories } = useGetAllCategoriesQuery()
  const { data: subCategories, isFetching: loadingSubCategories } = useGetSubCategoriesQuery(
    { categoryId: Number(currentCategory?.value) },
    { skip: !currentCategory },
  )

  const handleCreate = async () => {
    if (
      !adm0code ||
      !baseSchemaId ||
      !name ||
      adm0code === -1 ||
      !currentCategory ||
      !currentSubCategory ||
      !file ||
      !(fieldsData.questionLabelPosition && fieldsData.choiceLabelPosition)
    ) {
      showSnackBar("Please fill all required fields", "warning")
      return
    }
    try {
      const response = await saveXlsForm({
        baseXlsFormSchemaId: baseSchemaId,
        adm0code,
        name,
        file,
        questionLabelPosition: fieldsData.questionLabelPosition,
        choiceLabelPosition: fieldsData.choiceLabelPosition,
      }).unwrap()

      const message = "XLSForm added successfully"
      const warnings = response.warnings?.length
        ? response.warnings.map(item => `${item.message} ${item.list}`)?.join("\n")
        : ""
      showSnackBar(`${message}\n${warnings}`, "success")

      navigate(window.location.pathname.replace("add", `${response.xlsFormId}#mapping`))
    } catch (err) {
      const error = err as FetchBaseQueryError

      if (error.status === XlsFormStatus.ValidationError) {
        setErrorSaveXls(error.data as XlsFormResult)
      } else showSnackBar("An error occurred while processing your request", "error")

      window.scrollTo({
        top: validationRef.current?.offsetTop ?? 0,
        left: 0,
        behavior: "smooth",
      })
    }
  }

  const handleClear = () => {
    setFile(undefined)
    reset()
    setFieldsData({ questionLabelPosition: 0, choiceLabelPosition: 0 })
    setErrorSaveXls(null)
  }

  const handleCategories = (item: SingleValue<CommonGetOptions>): void => {
    setCurrentCategory(item)
    currentSubCategory && setCurrentSubCategory(null)
  }

  const handleSubCategories = (item: SingleValue<CommonGetOptions>): void => {
    setCurrentSubCategory(item)
  }

  const handleFileUpload = async (fileUpload: File): Promise<void> => {
    try {
      const formData = new FormData()
      formData.append("file", fileUpload)
      await saveXls(formData).unwrap()
    } catch (error) {
      handleClear()
      showSnackBar("An error occurred while processing your request", "error")
    }
  }

  useEffect(() => {
    if (file) handleFileUpload(file)
  }, [file])

  useEffect(() => {
    baseSchemaId && setBaseSchemaId(undefined)
    if (currentSubCategory) {
      setOptions(
        baseSchemas
          ?.filter(
            item =>
              item.xlsFormSubCategoryId === Number(currentSubCategory?.value) &&
              item.xlsFormsIsArchived === xlsFormsIsArchived,
          )
          ?.map(item => ({ value: item.xlsFormId?.toString(), name: item.xlsFormName })) ?? [],
      )
    } else {
      setOptions([])
    }
  }, [currentSubCategory])

  return (
    <div>
      <div className="space-y-5 w-50">
        <div>
          <CountrySelector
            isClearable={false}
            allowAll={false}
            label="Country"
            onSelect={country => setAdm0code(country?.value)}
            className="w-full"
          />
        </div>

        <TextInput
          labelText="Name"
          name="name"
          placeholder="Name of the xls form"
          type="text"
          onChange={e => setName(e.target.value)}
          className="border-gray-300 rounded-md text-base"
        />
        <div>
          <FormLabel htmlFor="category">Category</FormLabel>
          <Select
            inputId="category"
            isLoading={loadingCategories}
            placeholder="Select category"
            options={categories ?? []}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.value}
            value={categories?.find(cat => cat?.value === currentCategory?.value) ?? null}
            onChange={item => handleCategories(item)}
          />
        </div>
        <div>
          <FormLabel htmlFor="sub-category">Type</FormLabel>
          <Select
            inputId="sub-category"
            isLoading={loadingSubCategories}
            placeholder="Select type"
            options={subCategories ?? []}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.value}
            value={subCategories?.find(subCat => subCat?.value === currentSubCategory?.value) ?? null}
            onChange={item => handleSubCategories(item)}
          />
        </div>

        <div className="flex items-end justify-between">
          <div className="w-full">
            <div className="d-flex justify-between">
              <FormLabel htmlFor="base-xls">Base schema</FormLabel>
              <div className="d-flex wfp--label primary-color">
                <Checkbox
                  wrapperClassName="ms-4 !mb-0 flex-none"
                  labelText="Show archived"
                  onChange={e => {
                    setBaseSchemaId(0)
                    setxlsFormsIsArchived(e.target.checked)
                    setOptions(
                      baseSchemas
                        ?.filter(
                          item =>
                            item.xlsFormSubCategoryId === Number(currentSubCategory?.value) &&
                            item.xlsFormsIsArchived === e.target.checked,
                        )
                        ?.map(item => ({ value: item.xlsFormId?.toString(), name: item.xlsFormName })) ?? [],
                    )
                  }}
                  value="xlsFormsIsArchived"
                  id="xlsFormsIsArchived"
                />
              </div>
            </div>
            <Select
              inputId="base-xls"
              isLoading={loadingSchemas}
              isSearchable
              placeholder="Select base schema"
              options={options}
              getOptionLabel={option => option.name}
              getOptionValue={option => option.value}
              value={options.find(item => Number(item.value) === baseSchemaId) ?? null}
              onChange={item => setBaseSchemaId(Number(item?.value))}
            />
          </div>
        </div>

        <ExcelUploader clearAll={!file} onSelect={setFile} />
        {fileHeaders && (
          <XlsFormFieldMapping
            surveyList={fileHeaders.surveyHeaders}
            choiceList={fileHeaders.choicesHeaders}
            values={fieldsData}
            setValues={setFieldsData}
          />
        )}

        {errorSaveXls?.resultMessage && (
          <Blockquote kind="error" withIcon style={{ padding: "2rem" }}>
            {errorSaveXls.resultMessage}
          </Blockquote>
        )}

        {fileHeaders && errorSaveXls && (
          <div ref={validationRef}>
            <ValidationFormSchema data={errorSaveXls} />
          </div>
        )}

        {fileHeaders && !isLoading && (
          <div className="d-flex">
            {!errorSaveXls && <Button action={handleCreate} label="Save" className="btn-full blue mr-1" />}
            <Button action={handleClear} label="Clear & Re-Upload" className="btn-full blue " />
          </div>
        )}

        {isLoading && <InlineLoading description="Saving may take a couple of minutes..." />}
      </div>
    </div>
  )
}
